import { useState, useCallback } from "react";
import axios from "axios";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { useAuth } from "src/contexts/auth-provider";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { UPLOAD_CATEGORY_ITEM_IMAGE } from "src/constants/mutations";
import { mimeTypeToExtension } from "src/components/RestaurantSetting/RestaurantSettingComponent";

import { MutationCallbacks } from "src/interfaces/menu-items-props.interface";
import { UUID } from "src/types/shared.type";

export const useUploadItemImage = () => {
  const { axBe } = useAxiosInterceptor();
  const { user, hasRestaurant } = useAuth();
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const uploadItemImage = useCallback(async (
    menuItemCorrelationId: UUID,
    menuItemName: string,
    menuItemImageFile: File,
    menuCategoryName: string,
    callbacks?: MutationCallbacks
  ) => {
    setIsUploadingImage(true);
    try {
      const variables = {
        createItemPreSignedUrlInput: {
          storeUserCorrelationId: user?.storeUser?.correlationId,
          itemName: menuItemName,
          fileNameExtension: mimeTypeToExtension[menuItemImageFile?.type],
          storeCorrelationId: hasRestaurant?.correlationId,
          itemCorrelationId : menuItemCorrelationId,
          categoryName: menuCategoryName,
        },
      };

      const res = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPLOAD_CATEGORY_ITEM_IMAGE,
        variables: variables,
      });

      if (res.data.errors && res.data.errors.length > 0) {
        const errorMessages = res.data.errors
          .map((error: { message: string }) => error.message || error.toString())
          .join(", ");
        throw new Error(`GraphQL errors: ${errorMessages}`);
      }

      const awsUrl = res.data?.data?.createUploadPreSignedUrlForItem?.preSignedUrl;
      
      if (!awsUrl) {
        throw new Error("Failed to get pre-signed URL from response");
      }
      
      try {
        await axios.put(awsUrl, menuItemImageFile, {
          headers: {
            "Content-Type": menuItemImageFile?.type,
            "x-amz-acl": "public-read",
          },
        });

        callbacks?.onSuccess?.();
      } catch (uploadErr) {
        throw new Error(`Failed to upload image to AWS'}`);
      }
    } catch (err) {
      console.error("Error uploading item image:", err);
      callbacks?.onError?.(err);
      throw err;
    } finally {
      setIsUploadingImage(false);
    }
  }, [axBe, user?.storeUser?.correlationId, hasRestaurant?.correlationId]);

  return { uploadItemImage, isUploadingImage };
};

